/**
 * @file The analytics configuration document at `parameters/analytics`: the calendar periods
 * resolve on, the form stage lists, the closed states, which open items a stock counts, and the
 * line item status and kind lists with the date a held or released range uses, and the business
 * groups marked as analytics teams (`teamBusinessGroups`, read through `teamsOfUser`). Read by
 * `cloud_run/analytics/src/serving`, written only by the `saveAnalyticsConfig` callable, and
 * configured per tenant, with an emulator example in `data/examples`; the example held, released,
 * shipped and fulfilled status lists are provisional until checked against a sample of stored line
 * statuses. Every key is required except `ANALYTICS_OPTIONAL_KEYS`, the keys a named query may
 * declare, and `formDueDateField`, which no query reads; the
 * parser defaults only an absent `openItems` (`all`) and an absent `reconciliationClosedStates`
 * (`complete`), so the type, the parser and the example change together whenever a key is added.
 * Every list is bound into SQL as a parameter, never written into a statement.
 */
import type { AnalyticsCalendar, FactColumnId } from './Analytics.Catalog.types';
import type { DocId, FieldId } from './System.types';

export const ANALYTICS_PARAMETERS_PATH = 'parameters/analytics';

/** The fact columns a held or released line item range may be dated by. */
export const ANALYTICS_PERIOD_DATES = ['releaseDate', 'occurredAt'] as const satisfies readonly FactColumnId[];

export type AnalyticsPeriodDate = (typeof ANALYTICS_PERIOD_DATES)[number];

/** The open items a stock counts: every open item whatever its date, or only those dated in the period. */
export const ANALYTICS_OPEN_ITEMS = ['all', 'inPeriod'] as const;

export type AnalyticsOpenItems = (typeof ANALYTICS_OPEN_ITEMS)[number];

/** The optional keys, dotted as a refusal names them; an absent or malformed one refuses only the queries that declare it. */
export const ANALYTICS_OPTIONAL_KEYS = [
  'openItems',
  'lineItems.shipped',
  'lineItems.fulfilled',
  'stages.reconciliation',
  'reconciliationClosedStates',
  'teamBusinessGroups',
] as const;

export type AnalyticsOptionalKey = (typeof ANALYTICS_OPTIONAL_KEYS)[number];

export type AnalyticsParameters = {
  calendar: AnalyticsCalendar;
  stages: { order: string[]; quote: string[]; buy: string[]; reconciliation?: string[] };
  quoteClosedStates: string[];
  reconciliationClosedStates?: string[];
  openItems?: AnalyticsOpenItems;
  /** The business group ids that are analytics teams; a person's teams are their business groups in this list. */
  teamBusinessGroups?: DocId[];
  /** The form field whose value is a form's due date in My tasks; when absent, no form has a due date. */
  formDueDateField?: FieldId;
  lineItems: {
    held: string[];
    released: string[];
    lighting: string[];
    controls: string[];
    shipped?: string[];
    fulfilled?: string[];
    periodDate: AnalyticsPeriodDate;
  };
};
